import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import {
  toShiftPendingPostBookingRequirementsInput,
  usePendingPostBookingRequirementsForShifts,
} from "@src/appV2/redesign/Documents/api/wrappers/usePendingPostBookingRequirementsForShifts";
import { buildWorkplaceDocumentsPath } from "@src/appV2/redesign/Documents/paths";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { PostBookingDocumentsCardContent } from "./PostBookingDocumentsCardContent";

export interface PostBookingDocumentsCardProps {
  shift: Pick<Shift, "_id" | "agentReq" | "facility">;
}

export function PostBookingDocumentsCard(props: PostBookingDocumentsCardProps) {
  const { shift } = props;
  const history = useHistory();

  const workplaceId = shift.facility.userId;
  const qualification = shift.agentReq;

  const shiftInputs = useMemo(() => [toShiftPendingPostBookingRequirementsInput(shift)], [shift]);

  const { resultsByShiftId } = usePendingPostBookingRequirementsForShifts(shiftInputs);
  const count = resultsByShiftId[shift._id]?.count ?? 0;
  const shouldRender = isDefined(workplaceId) && count > 0;

  useLogEffect(
    APP_V2_USER_EVENTS.POST_BOOKING_DOCUMENTS_CAROUSEL_CARD_SHOWN,
    {
      shiftId: shift._id,
      workplaceId,
      qualification,
      count,
    },
    { enabled: shouldRender }
  );

  if (!shouldRender) {
    return null;
  }

  const onCompleteDocumentsClick = () => {
    logEvent(APP_V2_USER_EVENTS.POST_BOOKING_DOCUMENTS_CAROUSEL_CTA_CLICKED, {
      shiftId: shift._id,
      workplaceId,
      qualification,
      count,
    });
    history.push(
      buildWorkplaceDocumentsPath({
        workplaceId,
        qualification,
        shiftId: shift._id,
        postBookingRequirementsFilter: "include-only",
      })
    );
  };

  return (
    <PostBookingDocumentsCardContent
      count={count}
      onCompleteDocumentsClick={onCompleteDocumentsClick}
    />
  );
}
